import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
	getHelpAssets,
	getHelpDefaultFaq,
	getHelpTopics,
} from "../services/api-server/help_centre";
import { useHelpUploadsStore } from "../stores/helpUploadsStore";
import { useTenantStore } from "../stores/tenantStore";
import { HelpDefaultFaq, ReaderTopic } from "../types/helpCentre.types";
import {
	availableTopics,
	FAQ_PARAM,
	QUERY_PARAM,
	toReaderTopics,
} from "../utils/helpCentre/content";
import { buildHelpSearchIndex } from "../utils/helpCentre/search";
import useCheckModule from "./useCheckModule";

export const HELP_TOPICS_QUERY_KEY = ["help-centre", "topics"] as const;
export const HELP_FAQS_QUERY_KEY = ["help-centre", "faqs"] as const;
/** Links to the images in what this user can see, by id (`GET /assets`). */
export const HELP_ASSETS_QUERY_KEY = ["help-centre", "assets"] as const;

/** How long typing pauses before a search box writes `?q=`. */
const SEARCH_DELAY_MS = 250;

/**
 * Help Centre content is global, the same for every tenant, so the keys carry
 * no tenant: the module filter runs after the query, and switching tenant
 * reloads the page. Content rarely changes, so moving between pages reads the
 * cache. Retry once, except client errors (400/403), which a retry can't fix.
 */
export const HELP_QUERY_OPTIONS = {
	staleTime: 5 * 60_000,
	retry: (failureCount: number, error: unknown) => {
		const status = isAxiosError(error) ? error.response?.status : undefined;
		return failureCount < 1 && !(status && status >= 400 && status < 500);
	},
	refetchOnWindowFocus: false,
} as const;

export const useHelpTopics = (enabled = true) =>
	useQuery({
		queryKey: HELP_TOPICS_QUERY_KEY,
		queryFn: getHelpTopics,
		enabled,
		...HELP_QUERY_OPTIONS,
	});

/**
 * Whether this user may change Help Centre content: the server's `canEdit`
 * on the topics, from its `canEditHelpContent`. The one place the client
 * asks, so letting more people edit is a server change. `canEdit` stays
 * undefined until the topics have loaded.
 */
export const useCanEditHelp = () => {
	const query = useHelpTopics();
	return {
		canEdit: query.data?.canEdit,
		isError: !query.data && query.isError,
		isFetching: query.isFetching,
		refetch: query.refetch,
	};
};

/**
 * The topics this tenant's readers see: hidden content dropped (super-admins
 * are sent it) and topics for modules the tenant lacks left out. `topics`
 * stays undefined until the tenant has loaded as well as the topics: until
 * Home sets it, every module check fails, and a deep link to a module's topic
 * would flash "not available". Once there are topics, a failed refetch keeps
 * showing them.
 */
export const useReaderTopics = (enabled = true) => {
	const query = useHelpTopics(enabled);
	const tenant = useTenantStore((state) => state.tenant);
	const { checkModule } = useCheckModule();
	const topics = useMemo<ReaderTopic[] | undefined>(
		() =>
			query.data && tenant
				? availableTopics(toReaderTopics(query.data.topics), checkModule)
				: undefined,
		[query.data, tenant, checkModule]
	);
	return {
		topics,
		isError: !topics && query.isError,
		isPending: !topics && !query.isError,
		isFetching: query.isFetching,
		refetch: query.refetch,
	};
};

export const useHelpDefaultFaq = () => {
	const query = useQuery({
		queryKey: HELP_FAQS_QUERY_KEY,
		queryFn: getHelpDefaultFaq,
		...HELP_QUERY_OPTIONS,
	});
	return {
		defaultFaq: query.data,
		isError: !query.data && query.isError,
		isPending: !query.data && !query.isError,
		isFetching: query.isFetching,
		refetch: query.refetch,
	};
};

/**
 * Images whose link was missing from the loaded links and has been asked for
 * once more: once per image in the page's life, however often it remounts.
 */
const askedAgain = new Set<string>();

/**
 * The link to show an uploaded image by: the server's, for saved content, or
 * else one from an upload in this tab. The links are fetched only once an
 * image needs one. If the loaded links lack the id (someone has just saved
 * it), they are fetched once more for that image. `refresh` fetches them
 * again, for a link that stopped working.
 */
export const useHelpAssetUrl = (id: string) => {
	const uploaded = useHelpUploadsStore((state) => state.urls[id]);
	const query = useQuery({
		queryKey: HELP_ASSETS_QUERY_KEY,
		queryFn: getHelpAssets,
		...HELP_QUERY_OPTIONS,
	});
	const { refetch } = query;
	const url = query.data?.[id] ?? uploaded;
	const lacking = query.isSuccess && !url;

	useEffect(() => {
		if (!lacking || askedAgain.has(id)) return;
		askedAgain.add(id);
		refetch({ cancelRefetch: false });
	}, [lacking, id, refetch]);

	const refresh = useCallback(
		() => refetch({ cancelRefetch: false }),
		[refetch]
	);

	return {
		url,
		/** The links are loading, or being fetched again for this image. */
		isPending:
			!url &&
			(query.isPending ||
				(lacking && (!askedAgain.has(id) || query.isFetching))),
		isFetching: query.isFetching,
		refresh,
	};
};

/** The search index over what this tenant's readers see. */
export const useHelpSearchIndex = (
	topics: ReaderTopic[] | undefined,
	defaultFaq?: HelpDefaultFaq | null
) =>
	useMemo(
		() => buildHelpSearchIndex(topics ?? [], defaultFaq),
		[topics, defaultFaq]
	);

/**
 * A search box kept in `?q=`, so Back from a result returns to the results.
 * The URL holds the query the results use, so a navigation that changes it
 * (Back, Forward, or a link without `q`) changes the results in the same
 * render as the rest of the page. What's typed shows at once and reaches the
 * URL once typing pauses, with replace, so Back doesn't step through
 * keystrokes; a new query drops `?faq=`, whose item it would otherwise reopen.
 * Any other navigation drops what's still being typed.
 */
export const useSearchQueryParam = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { key: locationKey } = useLocation();
	const query = searchParams.get(QUERY_PARAM) ?? "";
	// What's typed but not yet in the URL.
	const [draft, setDraft] = useState<string | null>(null);
	const timer = useRef<number>();
	// The query the box has just written, so its own navigation is told apart.
	const ownWrite = useRef<string | null>(null);

	const setInput = useCallback(
		(value: string) => {
			setDraft(value);
			window.clearTimeout(timer.current);
			timer.current = window.setTimeout(() => {
				ownWrite.current = value;
				setSearchParams(
					(params) => {
						const next = new URLSearchParams(params);
						if (value) next.set(QUERY_PARAM, value);
						else next.delete(QUERY_PARAM);
						next.delete(FAQ_PARAM);
						return next;
					},
					{ replace: true }
				);
			}, SEARCH_DELAY_MS);
		},
		[setSearchParams]
	);

	useEffect(() => {
		const isOwnWrite = ownWrite.current === query;
		ownWrite.current = null;
		if (isOwnWrite) {
			// Keep anything typed since the write, and its timer.
			setDraft((typed) => (typed === query ? null : typed));
			return;
		}
		window.clearTimeout(timer.current);
		setDraft(null);
	}, [locationKey, query]);

	useEffect(() => () => window.clearTimeout(timer.current), []);

	return { input: draft ?? query, setInput, query };
};
